import * as React from 'react';
import { PageSection } from '@patternfly/react-core';
import { getAds, getCampaigns, getOrganizations } from "@app/utils/utils";
import { useEffect, useState } from "react";
import {
    BuildingIcon,
    CubesIcon,
    MonitoringIcon,
    RocketIcon,
    PlusCircleIcon,
    ArrowRightIcon,
    ChartLineIcon,
    LightbulbIcon
} from "@patternfly/react-icons";
import { useNavigate } from "react-router-dom";
import { useWindowSize, isMobile, isTablet } from "@app/utils/useWindowSize";

const Dashboard: React.FunctionComponent<any> = (props) => {

    const [organizationState, setOrganizationState] = useState<any>();
    const [adsState, setAdsState] = useState<any>();
    const [campaignsState, setCampaignsState] = useState<any>();
    const [isLoading, setIsLoading] = useState(true);
    const [hoveredCard, setHoveredCard] = useState<string | null>(null);
    const navigate = useNavigate();
    const { width } = useWindowSize();
    const mobile = isMobile(width);
    const tablet = isTablet(width);

    useEffect(() => {
        setIsLoading(true);

        Promise.all([
            getAds(),
            getOrganizations(),
            getCampaigns()
        ]).then(([ads, orgs, campaigns]) => {
            setAdsState(ads);
            setOrganizationState(orgs);
            setCampaignsState(campaigns);
            setIsLoading(false);

            // Redirect if no organizations
            const numberOfOrganizations = orgs ? (orgs.organizations ? orgs.organizations.length : 0) : 0;
            if (numberOfOrganizations === 0) {
                navigate("/organizations");
            }
        });
    }, []);

    const numberOfOrganizations = organizationState ? (organizationState.organizations ? organizationState.organizations.length : 0) : 0;
    const numberOfAds = adsState ? adsState.length : 0;
    const numberOfCampaigns = campaignsState ? campaignsState.length : 0;

    const styles = {
        container: {
            padding: mobile ? '16px' : (tablet ? '20px' : '24px'),
            maxWidth: '1200px',
            margin: '0 auto'
        },
        welcomeSection: {
            marginBottom: mobile ? '20px' : '32px'
        },
        welcomeTitle: {
            fontSize: mobile ? '24px' : '32px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 8px 0'
        },
        welcomeSubtitle: {
            fontSize: mobile ? '14px' : '16px',
            color: '#6b7280',
            margin: 0
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: mobile ? '16px' : '24px',
            marginBottom: mobile ? '24px' : '40px'
        },
        statCard: {
            background: '#ffffff',
            borderRadius: '16px',
            padding: '24px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            border: '1px solid #e5e7eb',
            position: 'relative' as const,
            overflow: 'hidden'
        },
        statCardHover: {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)'
        },
        statCardDecoration: {
            position: 'absolute' as const,
            top: '-20px',
            right: '-20px',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            opacity: 0.1
        },
        statIconContainer: {
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px',
            fontSize: '24px',
            color: '#ffffff'
        },
        statValue: {
            fontSize: '36px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 4px 0'
        },
        statLabel: {
            fontSize: '14px',
            color: '#6b7280',
            margin: 0,
            textTransform: 'capitalize' as const
        },
        statArrow: {
            position: 'absolute' as const,
            bottom: '24px',
            right: '24px',
            color: '#9ca3af',
            fontSize: '20px',
            transition: 'all 0.3s ease'
        },
        statArrowHover: {
            color: '#667eea',
            transform: 'translateX(4px)'
        },
        sectionTitle: {
            fontSize: '18px',
            fontWeight: 600,
            color: '#1f2937',
            margin: '0 0 20px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        },
        quickActionsGrid: {
            display: 'grid',
            gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: mobile ? '12px' : '16px',
            marginBottom: mobile ? '24px' : '40px'
        },
        quickActionCard: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '14px',
            padding: '20px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            color: '#ffffff'
        },
        quickActionCardHover: {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 30px rgba(102, 126, 234, 0.4)'
        },
        quickActionIcon: {
            width: '44px',
            height: '44px',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            flexShrink: 0
        },
        quickActionText: {
            fontSize: '15px',
            fontWeight: 600
        },
        tipsSection: {
            background: '#ffffff',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid #e5e7eb'
        },
        tipsList: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '16px'
        },
        tipItem: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px',
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '12px'
        },
        tipIcon: {
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontSize: '18px',
            flexShrink: 0
        },
        tipContent: {
            flex: 1
        },
        tipTitle: {
            fontSize: '14px',
            fontWeight: 600,
            color: '#1f2937',
            margin: '0 0 4px 0'
        },
        tipText: {
            fontSize: '13px',
            color: '#6b7280',
            margin: 0,
            lineHeight: 1.5
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            color: '#6b7280'
        }
    };

    const statCards = [
        {
            id: 'organizations',
            label: 'Organizations',
            value: numberOfOrganizations,
            icon: <BuildingIcon />,
            gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            path: '/organizations'
        },
        {
            id: 'ads',
            label: 'Ads',
            value: numberOfAds,
            icon: <CubesIcon />,
            gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            path: '/ads'
        },
        {
            id: 'campaigns',
            label: 'Campaigns',
            value: numberOfCampaigns,
            icon: <MonitoringIcon />,
            gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            path: '/campaigns'
        }
    ];

    const quickActions = [
        {
            id: 'create-ad',
            label: 'Create New Ad',
            icon: <PlusCircleIcon />,
            path: '/ads'
        },
        {
            id: 'create-campaign',
            label: 'Launch Campaign',
            icon: <RocketIcon />,
            path: '/campaigns'
        },
        {
            id: 'view-analytics',
            label: 'View Analytics',
            icon: <ChartLineIcon />,
            path: '/analytics'
        }
    ];

    const tips = [
        {
            title: 'Create engaging ads',
            text: 'Use high-quality images and compelling copy to capture attention. Ads with clear calls-to-action perform 2x better.'
        },
        {
            title: 'Target the right audience',
            text: 'Use geo-fencing campaigns to reach customers in specific locations. Perfect for local businesses!'
        },
        {
            title: 'Track your performance',
            text: 'Check your analytics regularly to understand what works. Optimize your campaigns based on real data.'
        }
    ];

    if (isLoading) {
        return (
            <PageSection style={{ minHeight: '100%', background: '#f8fafc' }}>
                <div style={styles.loadingContainer}>
                    <p>Loading dashboard...</p>
                </div>
            </PageSection>
        );
    }

    return (
        <PageSection style={{ minHeight: '100%', background: '#f8fafc' }}>
            <div style={styles.container}>
                {/* Welcome Section */}
                <div style={styles.welcomeSection}>
                    <h1 style={styles.welcomeTitle}>Welcome back!</h1>
                    <p style={styles.welcomeSubtitle}>
                        Here's what's happening with your advertising campaigns today.
                    </p>
                </div>

                {/* Stats Grid */}
                <div style={styles.statsGrid}>
                    {statCards.map(card => (
                        <div
                            key={card.id}
                            style={{
                                ...styles.statCard,
                                ...(hoveredCard === card.id ? styles.statCardHover : {})
                            }}
                            onMouseEnter={() => setHoveredCard(card.id)}
                            onMouseLeave={() => setHoveredCard(null)}
                            onClick={() => navigate(card.path)}
                        >
                            {/* Decoration */}
                            <div style={{
                                ...styles.statCardDecoration,
                                background: card.gradient
                            }} />

                            {/* Icon */}
                            <div style={{
                                ...styles.statIconContainer,
                                background: card.gradient
                            }}>
                                {card.icon}
                            </div>

                            {/* Value */}
                            <h2 style={styles.statValue}>{card.value}</h2>
                            <p style={styles.statLabel}>{card.label}</p>

                            {/* Arrow */}
                            <div style={{
                                ...styles.statArrow,
                                ...(hoveredCard === card.id ? styles.statArrowHover : {})
                            }}>
                                <ArrowRightIcon />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Quick Actions */}
                <h3 style={styles.sectionTitle}>
                    <RocketIcon style={{ color: '#667eea' }} />
                    Quick Actions
                </h3>
                <div style={styles.quickActionsGrid}>
                    {quickActions.map(action => (
                        <div
                            key={action.id}
                            style={{
                                ...styles.quickActionCard,
                                ...(hoveredCard === action.id ? styles.quickActionCardHover : {})
                            }}
                            onMouseEnter={() => setHoveredCard(action.id)}
                            onMouseLeave={() => setHoveredCard(null)}
                            onClick={() => navigate(action.path)}
                        >
                            <div style={styles.quickActionIcon}>
                                {action.icon}
                            </div>
                            <span style={styles.quickActionText}>{action.label}</span>
                        </div>
                    ))}
                </div>

                {/* Tips Section */}
                <h3 style={styles.sectionTitle}>
                    <LightbulbIcon style={{ color: '#f59e0b' }} />
                    Tips to Get Started
                </h3>
                <div style={styles.tipsSection}>
                    <div style={styles.tipsList}>
                        {tips.map((tip, index) => (
                            <div key={index} style={styles.tipItem}>
                                <div style={styles.tipIcon}>
                                    <LightbulbIcon />
                                </div>
                                <div style={styles.tipContent}>
                                    <h4 style={styles.tipTitle}>{tip.title}</h4>
                                    <p style={styles.tipText}>{tip.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </PageSection>
    );
}

export { Dashboard };
